import React, { useEffect, useState } from 'react';
import { ModalTitleAndClose, Modal, ModalTitle } from 'components/Modal';
import { isValidEmail, isValidTextOrNumberString } from 'utils/string';
import { useAppDispatch, useAppState } from 'store/configureStore';
import { apiAddUser } from 'store/action/userManagement';
import { AddUserModalContent } from 'components/UserTable/AddUserModalContent';
import { STRINGS } from 'config';

const {
  FEATURE_FLAGS: {
    PRACTICE_USER_ROLES_ENABLED,
  },
  REDUCERS: {
    USER_MANAGEMENT: {
      RESET_USER_CREATION_STATUS,
    },
  },
  USER_API_ADD_STATUS_CODES: {
    USER_ADDED_SUCCESS,
    USER_ALREADY_EXISTS,
  },
} = STRINGS;

export const initialState = {
  emailAddress: '',
  fullName: '',
  userRole: null,
  emailValidationError: null,
  fullNameValidationError: null,
  userRoleValidationError: null,
  shouldBlockUserActions: false,
  userCreationError: false,
};

export const AddUserModal = ({ isOpen, onClose }) => {
  const [emailAddress, setEmailAddress] = useState(initialState.emailAddress);
  const [emailValidationError, setEmailValidationError] = useState(initialState.emailValidationError);
  const [fullName, setFullName] = useState(initialState.fullName);
  const [fullNameValidationError, setFullNameValidationError] = useState(initialState.fullNameValidationError);
  const [userRole, setUserRole] = useState(initialState.userRole);
  const [shouldBlockUserActions, setShouldBlockUserActions] = useState(initialState.shouldBlockUserActions);
  const [userCreationError, setUserCreationError] = useState(initialState.userCreationError);
  const [userRoleValidationError, setUserRoleValidationError] = useState(initialState.userRoleValidationError);

  const state = useAppState();
  const dispatch = useAppDispatch();
  const {
    app: { selectedPractice, selectedPracticeUserRoles },
    userManagement: { userCreationStatus },
    featureFlags,
  } = state;

  const isPracticeUserRolesEnabled = featureFlags[PRACTICE_USER_ROLES_ENABLED];
  const isValidUserRole = (isPracticeUserRolesEnabled && userRole) || (!isPracticeUserRolesEnabled && !userRole);

  const onCloseCleanUp = () => {
    setShouldBlockUserActions(initialState.shouldBlockUserActions);
    setUserCreationError(initialState.userCreationError);
    setEmailAddress(initialState.emailAddress);
    setFullName(initialState.fullName);
    setUserRole(initialState.userRole);
    setEmailValidationError(false);
    setFullNameValidationError(false);
    setUserRoleValidationError(false);
    onClose();
  };

  const handleSubmitAddUser = () => {
    dispatch({ type: RESET_USER_CREATION_STATUS });
    setUserCreationError(false);
    setEmailValidationError(false);
    setFullNameValidationError(false);
    setUserRoleValidationError(false);
    setShouldBlockUserActions(true);
    const trimmedEmail = emailAddress.trim();
    const trimmedFullName = fullName.trim();
    setEmailAddress(trimmedEmail);
    setFullName(trimmedFullName);
    if (!isValidEmail(trimmedEmail)) {
      setEmailValidationError('Please enter a valid email address');
    }
    if (!isValidTextOrNumberString(trimmedFullName)) {
      setFullNameValidationError('Please enter a valid name');
    }
    if (!isValidUserRole) {
      setUserRoleValidationError('Please select a user role');
    }
    if (!isValidEmail(trimmedEmail) || !isValidTextOrNumberString(trimmedFullName) || !isValidUserRole) {
      return setShouldBlockUserActions(false);
    }
    return apiAddUser({
      practiceId: selectedPractice?.id,
      email: trimmedEmail,
      name: trimmedFullName,
      ...(isPracticeUserRolesEnabled ? { userRole } : {}),
      dispatch,
    })
      .then(resp => {
        if (resp.status === USER_ADDED_SUCCESS) {
          onCloseCleanUp();
        }
      });
  };

  const onChangeFullName = e => {
    setFullNameValidationError(false);
    setUserCreationError(false);
    setFullName(e.target.value);
  };
  const onChangeEmailAddress = e => {
    setEmailValidationError(false);
    setUserCreationError(false);
    setEmailAddress(e.target.value);
  };
  const onSelectUserRole = e => {
    setUserRoleValidationError(false);
    setUserCreationError(false);
    setUserRole(e);
  };

  useEffect(() => {
    if (userCreationStatus === USER_ALREADY_EXISTS) {
      setShouldBlockUserActions(false);
      setUserCreationError(true);
    }
    return () => {};
  }, [userCreationStatus]);

  const handleOnCloseUserOptionsModal = () => !shouldBlockUserActions && onCloseCleanUp();

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleOnCloseUserOptionsModal}
      modalContainerDataAutomationName="AddNewUserModal"
      id="UserOptionsModalId"
      aria-describedby="UserOptionsModalId"
      role="dialog"
    >
      <ModalTitleAndClose disabled={shouldBlockUserActions}>
        <ModalTitle data-automation="ModalTitleAddNewUser">Add new user</ModalTitle>
      </ModalTitleAndClose>
      <AddUserModalContent
        onClose={handleOnCloseUserOptionsModal}
        emailAddress={emailAddress}
        emailValidationError={emailValidationError}
        onChangeEmailAddress={onChangeEmailAddress}
        fullName={fullName}
        fullNameValidationError={fullNameValidationError}
        onChangeFullName={onChangeFullName}
        handleSubmitAddUser={handleSubmitAddUser}
        userCreationError={userCreationError}
        shouldBlockUserActions={shouldBlockUserActions}
        isPracticeUserRolesEnabled={isPracticeUserRolesEnabled}
        userRole={userRole}
        userRoles={selectedPracticeUserRoles}
        userRoleValidationError={userRoleValidationError}
        onSelectUserRole={onSelectUserRole}
      />
    </Modal>
  );
};
